/**
 * The shapes the core reasons about. Row shapes mirror `migrations/0001_init.sql`; the shell
 * maps snake_case columns to these. No student data has a field here, as in the schema.
 */

/** R9. Every profile has exactly one, and a new profile starts hidden. */
export const VISIBILITIES = ['hidden', 'labs', 'public'] as const;
export type Visibility = typeof VISIBILITIES[number];
export const DEFAULT_VISIBILITY: Visibility = 'hidden';

export function isVisibility(value: unknown): value is Visibility {
  return typeof value === 'string' && (VISIBILITIES as readonly string[]).includes(value);
}

/** The states Profile checks against a public educator lookup (R3, R4). */
export const VERIFIABLE_STATES = ['TX', 'FL'] as const;
export type VerifiableState = typeof VERIFIABLE_STATES[number];

export function isVerifiableState(state: string): state is VerifiableState {
  return (VERIFIABLE_STATES as readonly string[]).includes(state);
}

/** 'YYYY-MM'. A job carries months, not days. */
export type YearMonth = string;

export interface JobInput {
  role: string;
  schoolOrDistrict: string;
  startMonth: YearMonth;
  /** Null means the current role. */
  endMonth: YearMonth | null;
}

export interface AwardInput {
  name: string;
  issuer: string;
  year: number;
  note: string | null;
}

export interface CertificationInput {
  /** Two-letter postal code, upper case once validated. */
  state: string;
  area: string;
  /** Prefilled from the display name; may add a middle name the state record uses (KTD7). */
  nameOnCertificate: string;
  /** Florida's lookup key. Texas has none. */
  licenseNumber: string | null;
}

export interface Positioned { id: string; position: number }

export type Job = JobInput & Positioned;
export type Award = AwardInput & Positioned;
export type Certification = CertificationInput & Positioned;

export interface Profile {
  clerkUserId: string;
  displayName: string;
  title: string;
  /** The owner's 240 account photo (`core/identity`), or null for initials. */
  photoUrl: string | null;
  visibility: Visibility;
  slug: string;
  createdAt: string;
  updatedAt: string;
}

/** The lifecycle in the plan's HTD; visitors see a badge only in `verified` (KTD4). */
export const VERIFICATION_STATUSES = ['verified', 'unmatched', 'ambiguous', 'error'] as const;
export type VerificationStatus = typeof VERIFICATION_STATUSES[number];

/** What `lookup_runs.result` records for each attempt (KTD5). */
export const LOOKUP_RESULTS = ['ok', 'unmatched', 'ambiguous', 'error', 'blocked'] as const;
export type LookupResult = typeof LOOKUP_RESULTS[number];

/**
 * A badge is a snapshot (KTD7): what was checked, against what, and when. Editing any of these
 * fields on the certification, or moving the display name off `checkedName`, clears it.
 */
export interface VerificationSnapshot {
  certificationId: string;
  status: VerificationStatus;
  /**
   * The name on the certificate as checked, whitespace collapsed, so the visitor reads it as the
   * owner wrote it (R19). Always compared through `normalizeName`.
   */
  checkedName: string;
  /** Why a check did not end in a badge (`core/verify/match` OWNER_REASONS); null when verified. */
  reason?: string | null;
  state: string;
  area: string;
  /** Florida only. */
  licenseNumber: string | null;
  /**
   * TEA's record id when the lookup exposed one. Nullable: a single-match TEA search returns the
   * certificate page directly with no `spid` (U1 fixtures README).
   */
  recordId: string | null;
  checkedAt: string;
}

/** The three entry sections, as tables and as the `/api/me/:section` path segment. */
export const SECTION_NAMES = ['certifications', 'jobs', 'awards'] as const;
export type SectionName = typeof SECTION_NAMES[number];

/** Why a field was refused. */
export type ValidationReason = 'required' | 'invalid' | 'before_start' | 'out_of_range' | 'too_long';

export type Validation<T, F extends string> =
  | { ok: true; value: T }
  | { ok: false; field: F; reason: ValidationReason };
